import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  IconButton,
  InputAdornment,
  InputBase,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
  ListItemIcon,
} from "@mui/material";
import {
  Search as SearchIcon,
  WatchLaterOutlined,
  CalendarMonthOutlined,
  PlaceOutlined,
} from "@mui/icons-material";
import GraceBot from "../../assets/icons/grace-mascot.png";
import Banner from "../../assets/media/banner-puzzle.jpg";
import { immediateNeedSchema, preneedSchema } from "../../schemas/needSchema";
import { SearchResult, Service } from "./homepage-new-types";
import AmericaFlag from "../../assets/icons/america-flag.svg";
import IndiaFlag from "../../assets/icons/Indian-Flag.svg";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchSuggestions,
  getCityCoordinates,
  getCityName,
} from "../../api/mapbox";
import {
  getSearchResults,
  getServices,
  getTopProviderAndPlanar,
} from "../../api/homepage.api";
import { getProviderList } from "../../api/listing-page.api";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useDebounce } from "../../common/hooks/useDebounce";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import "./first-section.scss";
import { getCategories } from "../../api/admin/service-management";
import { getTopThreeProviderAndPlanner } from "./search-helper";

const COUNTRY_PHONE_MAP: Record<
  string,
  { dialCode: string; flag: string; isoCode: string }
> = {
  US: { dialCode: "+1", flag: AmericaFlag, isoCode: "US" },
  IN: { dialCode: "+91", flag: IndiaFlag, isoCode: "IN" },
};
const DEFAULT_COUNTRY = COUNTRY_PHONE_MAP.US;

type LocationSuggestion = {
  main: string;
  secondary: string;
  raw: any;
};

export default function FirstSectionHome({
  categories,
}: {
  categories: any[];
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const enterPressed = useRef<boolean>(false);
  const [previousSearch, setPreviousSearch] = useState<string>("");

  const { city } = useSelector((state: any) => state.userLocation);
  const [tabValue, setTabValue] = useState(0);
  const [query, setQuery] = useState("");
  const [displayLocation, setDisplayLocation] = useState<string>("");
  const [manualCity, setManualCity] = useState<string>("");
  const [cityError, setCityError] = useState<string>("");
  const [isManualSelection, setIsManualSelection] = useState(false);
  const [immediateNeedServices, setImmediateNeedServices] = useState<Service[]>(
    [],
  );
  const [preneedServices, setPreneedServices] = useState<Service[]>([]);
  const [immediateNeedCategories, setImmediateNeedCategories] = useState<any[]>(
    [],
  );
  const [preNeedCategories, setPreNeedCategories] = useState<any[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [isLocationSelected, setIsLocationSelected] = useState(false);

  const [immediateNeedForm, setImmediateNeedForm] = useState({
    location: "",
    name: "",
    phone: "",
    serviceType: "",
    needType: "",
  });

  const [preneedForm, setPreneedForm] = useState({
    location: "",
    name: "",
    phone: "",
    serviceType: "",
    dateTime: "",
    needType: "",
  });
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [timeWindow, setTimeWindow] = useState<string>("");
  const [datePickerKey, setDatePickerKey] = useState(0);
  const [isImmediateNeedButtonDisabled, setIsImmediateNeedButtonDisabled] =
    useState(true);
  const [isPreneedButtonDisabled, setIsPreneedButtonDisabled] = useState(true);
  const debouncedManualCity = useDebounce(manualCity, 700);
  const dispatch = useDispatch();
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);

  const handleTabChange = (event: any, newValue: number) => {
    setTabValue(newValue);
  };

  const handleChange = (e: any, formType: "immediate" | "preneed") => {
    const { name, value } = e.target;

    let updatedValue = value;
    if (name === "name") {
      updatedValue = value.replace(/[^A-Za-z\s'-]/g, "");
    }
    if (formType === "immediate") {
      setImmediateNeedForm((prev) => ({
        ...prev,
        [name]: updatedValue,
      }));
    } else {
      setPreneedForm((prev) => ({ ...prev, [name]: updatedValue }));
    }
  };

  const handleImmediateNeedChange = (e: any) => {
    handleChange(e, "immediate");
  };

  const handlePreneedChange = (e: any) => {
    handleChange(e, "preneed");
  };

  const handleReset = (formType: "immediate" | "preneed") => {
    setManualCity("");
    setSuggestions([]);
    setIsTyping(false);
    setIsLocationSelected(false);
    setSelectedCountry(DEFAULT_COUNTRY);

    dispatch({
      type: "USER_LOCATION:SET_LOCATION",
      payload: {
        latitude: 0,
        longitude: 0,
        city: "",
      },
    });

    localStorage.setItem(
      "user_location",
      JSON.stringify({
        longitude: 0,
        latitude: 0,
        city: "",
      }),
    );

    if (formType === "immediate") {
      setImmediateNeedForm({
        location: "",
        name: "",
        phone: "",
        serviceType: "",
        needType: "",
      });
    } else {
      setPreneedForm({
        location: "",
        name: "",
        phone: "",
        serviceType: "",
        dateTime: "",
        needType: "",
      });
      setSelectedDate(null);
      setTimeWindow("");
      setDatePickerKey((prev) => prev + 1);
    }

    setDisplayLocation("");
  };

  const handleImmediateNeedReset = () => {
    handleReset("immediate");
  };

  const handlePreneedReset = () => {
    handleReset("preneed");
  };

  //   LOCATION HANDLERS
  const handleLocationOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^a-zA-Z\s]/g, "");

    setManualCity(value);
    setCityError("");
    setIsTyping(true);
    setIsLocationSelected(false);

    setImmediateNeedForm((prev) => ({ ...prev, location: "" }));
    setPreneedForm((prev) => ({ ...prev, location: "" }));

    if (!value.trim()) {
      setSuggestions([]);
      setSelectedCountry(DEFAULT_COUNTRY);
    }
  };

  const handleSuggestionClick = async (suggestion: LocationSuggestion) => {
    const [longitude, latitude] = suggestion.raw?.center || [];

    if (longitude == null || latitude == null) return;

    const countryMeta = getCountryFromMapboxFeature(suggestion.raw);
    const cityLabel = suggestion.main;
    const displayLabel = suggestion.secondary
      ? `${suggestion.main}, ${suggestion.secondary}`
      : suggestion.main;

    setManualCity(displayLabel);
    setDisplayLocation(displayLabel);
    setIsManualSelection(true);
    setSuggestions([]);
    setCityError("");
    setIsTyping(false);
    setIsLocationSelected(true);
    setSelectedCountry(countryMeta);

    const locationString = `${latitude}, ${longitude}`;
    setImmediateNeedForm((prev) => ({ ...prev, location: locationString }));
    setPreneedForm((prev) => ({ ...prev, location: locationString }));

    localStorage.setItem(
      "user_location",
      JSON.stringify({ latitude, longitude, city: cityLabel }),
    );
  };

  useEffect(() => {
    if (categories && categories.length > 0) {
      const atNeed = categories.filter((cat: any) =>
        cat.needTypes?.includes("at_need"),
      );
      const preNeed = categories.filter((cat: any) =>
        cat.needTypes?.includes("pre_need"),
      );
      setImmediateNeedCategories(atNeed);
      setPreNeedCategories(preNeed);
    }
  }, [categories]);

  // AUTOSUGGESTION
  useEffect(() => {
    const fetchCitySuggestions = async (query: string) => {
      try {
        const response = await fetchSuggestions(query);

        const cityNames: LocationSuggestion[] =
          response?.data?.features?.map((item: any) => {
            const city = item.text;

            const region = item.context?.find((c: any) =>
              c.id.startsWith("region"),
            )?.text;

            const country = item.context?.find((c: any) =>
              c.id.startsWith("country"),
            )?.text;

            const secondaryText = [region, country].filter(Boolean).join(", ");

            return {
              main: city,
              secondary: secondaryText,
              raw: item,
            };
          }) || [];

        setSuggestions(cityNames);
      } catch (error) {
        console.error("Failed to fetch city suggestions:", error);
        setSuggestions([]);
      }
    };

    if (isTyping && debouncedManualCity && debouncedManualCity.length >= 2) {
      fetchCitySuggestions(debouncedManualCity);
    } else {
      setSuggestions([]);
    }
  }, [debouncedManualCity, isTyping]);

  useEffect(() => {
    const { location, name, phone, serviceType } = immediateNeedForm;
    const isPhoneValid = phone.length === 10 && /^\d{10}$/.test(phone);
    const isLocationValid =
      manualCity.trim().length > 0 && (isLocationSelected || !isTyping);
    setIsImmediateNeedButtonDisabled(
      !location ||
        name.trim().length < 2 ||
        !isPhoneValid ||
        !serviceType ||
        !isLocationValid,
    );
  }, [immediateNeedForm, manualCity, isLocationSelected, isTyping]);

  useEffect(() => {
    const { location, name, phone, serviceType, dateTime } = preneedForm;
    const isPhoneValid = phone.length === 10 && /^\d{10}$/.test(phone);
    const isLocationValid =
      manualCity.trim().length > 0 && (isLocationSelected || !isTyping);
    setIsPreneedButtonDisabled(
      !location ||
        name.trim().length < 2 ||
        !isPhoneValid ||
        !serviceType ||
        !dateTime ||
        !isLocationValid,
    );
  }, [preneedForm, manualCity, isLocationSelected, isTyping]);

  const handleFindServices = async (
    form: any,
    schema: any,
    needType: "immediate" | "preneed",
    apiNeedType: "at_need" | "pre_need",
  ) => {
    try {
      const storedLocation = localStorage.getItem("user_location");
      let latitude = null;
      let longitude = null;

      if (storedLocation) {
        const loc = JSON.parse(storedLocation);
        latitude = loc.latitude;
        longitude = loc.longitude;
      }

      const formToValidate = {
        ...form,
        needType: needType,
      };

      await schema.validate(formToValidate, { abortEarly: false });
      const response = await getProviderList(
        Number(form.serviceType),
        apiNeedType,
        latitude,
        longitude,
        form.name,
        form.phone,
        1,
        10,
      );
      console.log(
        "data we are sending to listing page FORM TO VALIDATE",
        formToValidate,
      );
      console.log("provider list response", response.data.data.data);
      navigate("/home/list-of-funeral-homes", {
        state: {
          ...formToValidate,
          latitude,
          longitude,
          providers: response.data.data.data,
          totalCount: response.data.data.totalCount,
        },
      });
    } catch (validationError: any) {
      console.error("Validation failed:", validationError);
    }
  };

  const handleImmediateNeedSubmit = async () => {
    await handleFindServices(
      immediateNeedForm,
      immediateNeedSchema,
      "immediate",
      "at_need",
    );
  };

  const handlePreneedSubmit = async () => {
    await handleFindServices(preneedForm, preneedSchema, "preneed", "pre_need");
  };
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const { topProviders, topPlanners } = useSelector(
    (state: any) => state.search,
  );
  const debouncedQuery = useDebounce(query, 700);

  const handleResultClick = (result: SearchResult) => {
    addRecentSearch(result?.name);
    if (result.type === "Provider") {
      navigate(`list-of-funeral-homes/details/${result.id}`);
    } else if (result.type === "Preneed Planner") {
      navigate(`list-of-funeral-homes/preneed-planners/${result.id}`);
    } else if (result.type === "service") {
      navigate(`list-of-funeral-homes`, {
        state: { searchServiceId: result.id },
      });
    } else if (result.type === "serviceCategory") {
      navigate(`list-of-funeral-homes?serviceCategoryId=${result.id}`);
    }
  };

  const handleTopProviderPlannerClick = (result: SearchResult) => {
    if (result.type === "Preneed Planner" || result.type === "planner") {
      navigate(`list-of-funeral-homes/preneed-planners/${result.id}`);
    } else {
      navigate(`list-of-funeral-homes/details/${result.id}`);
    }
  };

  const handleSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    setPreviousSearch(String(searchQuery));
    addRecentSearch(searchQuery);
    try {
      const response = await getSearchResults(searchQuery);
      let results = [];
      if (response.data.success && !response.data.data?.message) {
        results = response.data.data || [];
      } else {
        results = [];
      }
      setSearchResults(results);
      if (results?.length === 0) {
        setSearchResults([]);
        dispatch({
          type: "SEARCH:SET_CHAT_BOAT_MESSAGE",
          payload: {
            openChatBot: true,
            searchText: searchQuery,
          },
        });
      }
    } catch (error) {
      console.error("Search failed:", error);
      setSearchResults([]);
    }
  }, []);

  useEffect(() => {
    if (enterPressed.current) {
      enterPressed.current = false;
    } else {
      handleSearch(debouncedQuery);
    }
  }, [debouncedQuery, handleSearch]);

  useEffect(() => {
    const fetchAllServices = async () => {
      try {
        const [atNeedResponse, preNeedResponse] = await Promise.all([
          getServices("at_need"),
          getServices("pre_need"),
        ]);

        const atNeedServices = atNeedResponse?.data?.data?.data || [];
        const preNeedServices = preNeedResponse?.data?.data?.data || [];

        setImmediateNeedServices(atNeedServices);
        setPreneedServices(preNeedServices);
      } catch (error) {
        console.error("Failed to fetch one or more services:", error);
      }
    };

    getTopThreeProviderAndPlanner(dispatch);
  }, []);

  useEffect(() => {
    const fetchLocation = async () => {
      const storedLocation = localStorage.getItem("user_location");

      if (!storedLocation) return;

      try {
        const { latitude, longitude } = JSON.parse(storedLocation);

        const response = await getCityName(latitude, longitude);
        const features = response.data?.features;

        if (features?.length > 0) {
          const feature = features[0];
          const city = feature.text || "Please enter location manually";
          const countryMeta = getCountryFromMapboxFeature(feature);

          setDisplayLocation(city);
          setManualCity(city);
          setSelectedCountry(countryMeta);
        }

        setImmediateNeedForm((prev) => ({
          ...prev,
          location: `${latitude}, ${longitude}`,
        }));

        setPreneedForm((prev) => ({
          ...prev,
          location: `${latitude}, ${longitude}`,
        }));

        setIsLocationSelected(true);
        setIsTyping(false);
      } catch (err) {
        console.error("Failed to reverse geocode location:", err);
      }
    };

    fetchLocation();
  }, []);

  useEffect(() => {}, [displayLocation]);

  const debouncedLocation = useDebounce(displayLocation, 600);

  useEffect(() => {
    const fetchCityCoordinates = async () => {
      if (!debouncedLocation || debouncedLocation.length < 2) return;

      try {
        const response = await getCityCoordinates(debouncedLocation);

        if (response.data?.features?.length > 0) {
          const feature = response.data.features[0];
          const { center, text } = feature;
          const [lon, lat] = center;
          const countryMeta = getCountryFromMapboxFeature(feature);

          localStorage.setItem(
            "user_location",
            JSON.stringify({ latitude: lat, longitude: lon, city: text }),
          );

          setDisplayLocation(text);
          setSelectedCountry(countryMeta);

          setImmediateNeedForm((prev) => ({
            ...prev,
            location: `${lat}, ${lon}`,
          }));
          setPreneedForm((prev) => ({
            ...prev,
            location: `${lat}, ${lon}`,
          }));

          setIsLocationSelected(true);
          setIsTyping(false);
        }
      } catch (error) {
        console.error("Error fetching city coordinates:", error);
      }
    };

    fetchCityCoordinates();
  }, [debouncedLocation]);

  // (Replaced by unified validation with location above)

  useEffect(() => {
    if (city && city !== manualCity) {
      setManualCity(city);
      setIsTyping(false);
      setIsLocationSelected(true);
    }
  }, [city]);

  useEffect(() => {
    if (manualCity.length > 1) {
      setIsLocationSelected(false);
    }
  }, [manualCity]);

  const handleDateChange = (newValue: Dayjs | null) => {
    if (newValue && newValue.isValid()) {
      const today = dayjs().startOf("day");
      if (newValue.startOf("day").isBefore(today)) {
        setSelectedDate(null);
      } else {
        setSelectedDate(newValue.startOf("day"));
      }
    } else {
      setSelectedDate(null);
    }
  };

  useEffect(() => {
    if (selectedDate && timeWindow) {
      const startHourMap: Record<string, number> = {
        morning: 9,
        afternoon: 12,
        evening: 17,
      };
      const iso = selectedDate
        .hour(startHourMap[timeWindow])
        .minute(0)
        .second(0)
        .millisecond(0)
        .toISOString();
      setPreneedForm((prev) => ({ ...prev, dateTime: iso }));
    } else if (selectedDate) {
      setPreneedForm((prev) => ({
        ...prev,
        dateTime: selectedDate.toISOString(),
      }));
    } else {
      setPreneedForm((prev) => ({ ...prev, dateTime: "" }));
    }
  }, [selectedDate, timeWindow]);

  const TimeWindowActionBar = (props: any) => {
    const { timeWindow, setTimeWindow } = props;

    const handleChange = (
      _e: React.MouseEvent<HTMLElement>,
      newWindow: string | null,
    ) => {
      if (newWindow !== null) setTimeWindow(newWindow);
    };
    return (
      <Box sx={{ p: 1, pt: 1.5, pr: 3 }}>
        <Typography
          variant="caption"
          sx={{
            display: "flex",
            mb: 3,
            mt: 1,
            color: "#212121",
            fontSize: 14,
          }}
        >
          Select a time slot
        </Typography>
        <ToggleButtonGroup
          exclusive
          value={timeWindow}
          onChange={handleChange}
          sx={{
            display: "flex",
            flexDirection: "column",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <ToggleButton
            value="morning"
            sx={{ textTransform: "none", px: 2, minHeight: 36, fontSize: 12 }}
          >
            Morning
          </ToggleButton>
          <ToggleButton
            value="afternoon"
            sx={{ textTransform: "none", px: 2, minHeight: 36, fontSize: 12 }}
          >
            Afternoon
          </ToggleButton>
          <ToggleButton
            value="evening"
            sx={{ textTransform: "none", px: 2, minHeight: 36, fontSize: 12 }}
          >
            Evening
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    );
  };

  const getCountryFromMapboxFeature = (feature: any) => {
    if (!feature) return DEFAULT_COUNTRY;

    const countryContext = feature.context?.find((item: any) =>
      item.id?.startsWith("country"),
    );

    const countryCode =
      countryContext?.short_code?.toUpperCase() ||
      countryContext?.properties?.short_code?.toUpperCase() ||
      feature?.properties?.short_code?.toUpperCase();

    if (countryCode && COUNTRY_PHONE_MAP[countryCode]) {
      return COUNTRY_PHONE_MAP[countryCode];
    }

    return DEFAULT_COUNTRY;
  };

  const handleDetectLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setCityError("Geolocation is not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          const response = await getCityName(latitude, longitude);
          const feature = response?.data?.features?.[0];

          if (!feature) {
            setCityError("Could not detect city. Please enter manually.");
            return;
          }

          const detectedCity = feature.text || "";
          const countryMeta = getCountryFromMapboxFeature(feature);

          setManualCity(detectedCity);
          setDisplayLocation(detectedCity);
          setCityError("");
          setSuggestions([]);
          setIsTyping(false);
          setIsLocationSelected(true);
          setSelectedCountry(countryMeta);

          setImmediateNeedForm((prev) => ({
            ...prev,
            location: `${latitude}, ${longitude}`,
          }));

          setPreneedForm((prev) => ({
            ...prev,
            location: `${latitude}, ${longitude}`,
          }));

          dispatch({
            type: "USER_LOCATION:SET_LOCATION",
            payload: {
              latitude,
              longitude,
              city: detectedCity,
            },
          });

          localStorage.setItem(
            "user_location",
            JSON.stringify({
              latitude,
              longitude,
              city: detectedCity,
            }),
          );
        } catch (error) {
          console.error("Error detecting city:", error);
          setCityError("Could not detect city. Please enter manually.");
        }
      },
      (error) => {
        console.error("Geolocation error:", error.message);
        setCityError("Could not detect city. Please enter manually.");
      },
    );
  }, [dispatch]);

  useEffect(() => {
    const storedSearches = localStorage.getItem("home_page_recent_searches");
    if (storedSearches) {
      try {
        const parsedSearches = JSON.parse(storedSearches);
        if (Array.isArray(parsedSearches)) {
          setRecentSearches(parsedSearches);
        }
      } catch (error) {
        // Clear invalid data
        localStorage.removeItem("home_page_recent_searches");
      }
    }
  }, []);

  const addRecentSearch = useCallback((searchTerm: string) => {
    if (!searchTerm || searchTerm.trim() === "") return;

    try {
      // Always get the most recent searches from localStorage to avoid stale state
      const storedSearches = localStorage.getItem("home_page_recent_searches");
      const currentSearches = storedSearches ? JSON.parse(storedSearches) : [];

      const updatedSearches = [
        searchTerm,
        ...currentSearches.filter((search: string) => search !== searchTerm),
      ].slice(0, 5);
      setRecentSearches(updatedSearches);
      localStorage.setItem(
        "home_page_recent_searches",
        JSON.stringify(updatedSearches),
      );
    } catch (error) {
      console.error("Error in addRecentSearch:", error);
    }
  }, []);

  const removeRecentSearch = useCallback((searchTerm: string) => {
    if (!searchTerm || searchTerm.trim() === "") return;

    try {
      // Always get the most recent searches from localStorage to avoid stale state
      const storedSearches = localStorage.getItem("home_page_recent_searches");
      const currentSearches = storedSearches ? JSON.parse(storedSearches) : [];

      const updatedSearches = currentSearches.filter(
        (search: string) => search !== searchTerm,
      );
      setRecentSearches(updatedSearches);
      localStorage.setItem(
        "home_page_recent_searches",
        JSON.stringify(updatedSearches),
      );
    } catch (error) {
      console.error("Error in removeRecentSearch:", error);
    }
  }, []);

  const clearRecentSearches = useCallback(() => {
    try {
      setRecentSearches([]);
      localStorage.removeItem("home_page_recent_searches");
    } catch (error) {
      console.error("Error in clearRecentSearches:", error);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node) &&
        !query
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [query]);

  useEffect(() => {
    return () => {
      setShowSuggestions(false);
    };
  }, []);

  return (
    <Box component="section" className="search-bar-img-tags">
      <Container maxWidth="xl" className="bg-image">
        <Box
          sx={{
            position: "relative",
            width: "100%",
            // minHeight: { xs: "500px", md: "600px" },
            overflow: "hidden",
            maxHeight: { lg: "565px" },
          }}
        >
          {/* Background Image */}
          <Box
            component="img"
            src={Banner}
            alt="Background"
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "57.5%",
              height: "104%",
              objectFit: "fill",
              zIndex: 0,
            }}
          />

          {/* Custom Gradient Mask */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "60%",
              height: "100%",
              background: `linear-gradient(to right,
                                  #FFF7DC 0%,
                                  #FFF7DC 40%,
                                  rgba(255, 247, 220, 0.95) 50%,
                                  rgba(255, 247, 220, 0.5) 60%,
                                  rgba(255, 247, 220, 0.15) 70%,
                                  rgba(255, 247, 220, 0) 80%)`,
              zIndex: 1,
            }}
          />

          {/* Foreground Content */}
          <Container
            sx={{
              position: "relative",
              zIndex: 2,
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "start", md: "center" },
              gap: { xs: 4, md: 6 },
              py: { xs: 4, md: 8 },
              paddingLeft: { xs: 0 },
              maxWidth: { md: "1200px" },
            }}
          >
            {/* Left Side */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 3,
              }}
            >
              {/* grace bot whole  */}
              <Box
                sx={{
                  borderRadius: "0.5rem",
                  padding: { xs: "1rem", md: "1.5rem" },
                  paddingTop: { md: "0.3rem" },
                  paddingBottom: { md: "0.3rem" },
                  bgcolor: "#b9c7b6",
                  position: "relative",
                  width: { xs: "80%", sm: "90%", md: "26.625rem" },
                  height: { xs: "auto", md: "8.375rem" },
                  mx: { xs: "auto", md: 0 },
                  boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
                  paddingLeft: { md: "2.5rem" },
                  marginTop: { lg: "1rem" },
                }}
                ref={searchRef}
              >
                <Box sx={{ position: "relative", mt: 2 }}>
                  <Typography
                    sx={{
                      marginLeft: { xs: "2.5rem", md: "3rem" },
                      fontSize: { xs: "1rem", md: "1.125rem" },
                      color: "#131313",
                      mb: 1,
                    }}
                  >
                    Hi! I&apos;m GRACE. How can I help you today?
                  </Typography>
                  <Box sx={{ position: "relative", width: "100%" }}>
                    <Box
                      component="img"
                      src={GraceBot}
                      alt="grace-bot"
                      sx={{
                        position: "absolute",
                        top: "51%",
                        left: 14,
                        transform: "translate(-30%, -50%)",
                        width: { xs: 61, md: 62 },
                        height: { xs: 61, md: 62 },
                        borderRadius: "50%",
                        zIndex: 2,
                      }}
                    />

                    <Box sx={{ position: "relative", width: "100%" }}>
                      <InputBase
                        fullWidth
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e?.key === "Enter" || e?.code === "Enter") {
                            enterPressed.current = true;
                            handleSearch(query);
                          }
                        }}
                        onFocus={() => setShowSuggestions(true)}
                        placeholder="Your personal guide for funeral and memorial planning"
                        sx={{
                          borderRadius: 2,
                          backgroundColor: "#fff",
                          paddingLeft: { xs: "16%", sm: "10%", md: "65px" },
                          height: { xs: "2.5rem", md: "2.563rem" },
                          fontSize: { xs: 10, sm: 14, md: 13 },
                          paddingRight: "2rem",
                        }}
                      />
                      <IconButton
                        onClick={() => handleSearch(query)}
                        sx={{
                          position: "absolute",
                          right: 6,
                          top: "50%",
                          transform: "translateY(-50%)",
                          backgroundColor: "#14585d",
                          color: "#fff",
                          padding: "0.2rem",
                          borderRadius: "0.5rem",
                          "&:hover": {
                            backgroundColor: "#14585d",
                          },
                        }}
                      >
                        <SearchIcon />
                      </IconButton>
                    </Box>

                    {/* Search Results Dropdown */}
                    {debouncedQuery && searchResults?.length > 0 && (
                      <Paper className="list-paper">
                        <List sx={{ bgcolor: "#fff", p: 0 }}>
                          {searchResults.map((result, index) => (
                            <ListItem
                              key={index}
                              sx={{
                                "&:hover": {
                                  backgroundColor: "#f5f5f5",
                                },
                                cursor: "pointer",
                              }}
                              onClick={() => handleResultClick(result)}
                            >
                              <ListItemText
                                primary={result.name}
                                sx={{ color: "#131313" }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    )}
                    {debouncedQuery &&
                      topPlanners?.length === 0 &&
                      topProviders?.length === 0 &&
                      searchResults?.length === 0 && (
                        <Paper className="list-paper">
                          <Box style={{ padding: "4px 16px" }}>
                            <Typography sx={{ color: "#131313" }}>
                              No results found
                            </Typography>
                          </Box>
                        </Paper>
                      )}
                    {showSuggestions &&
                      !debouncedQuery &&
                      searchResults?.length === 0 && (
                        <Paper className="list-paper">
                          <List sx={{ bgcolor: "#fff", p: 0 }}>
                            {topProviders?.length > 0 && (
                              <>
                                <ListItem
                                  key="provider-0"
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "#f5f5f5",
                                    },
                                    cursor: "not-allowed;",
                                    fontStyle: "italic",
                                  }}
                                >
                                  <ListItemText
                                    primary="Top Providers"
                                    sx={{ color: "#767373" }}
                                  />
                                </ListItem>

                                {topProviders.map((result, index) => (
                                  <ListItem
                                    key={index}
                                    sx={{
                                      "&:hover": {
                                        backgroundColor: "#f5f5f5",
                                      },
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      handleTopProviderPlannerClick(result)
                                    }
                                  >
                                    <ListItemText
                                      primary={result.name}
                                      sx={{ color: "#131313" }}
                                    />
                                  </ListItem>
                                ))}
                              </>
                            )}

                            {topPlanners?.length > 0 && (
                              <>
                                <ListItem
                                  key="planner-0"
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "#f5f5f5",
                                    },
                                    cursor: "not-allowed;",
                                    fontStyle: "italic",
                                  }}
                                >
                                  <ListItemText
                                    primary="Top Planners"
                                    sx={{ color: "#767373" }}
                                  />
                                </ListItem>
                                {topPlanners.map((result, index) => (
                                  <ListItem
                                    key={index}
                                    sx={{
                                      "&:hover": {
                                        backgroundColor: "#f5f5f5",
                                      },
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      handleTopProviderPlannerClick(result)
                                    }
                                  >
                                    <ListItemText
                                      primary={result.name}
                                      sx={{ color: "#131313" }}
                                    />
                                  </ListItem>
                                ))}
                              </>
                            )}
                            {topPlanners?.length === 0 &&
                              topProviders?.length === 0 && (
                                <ListItem
                                  key="planner-0"
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "#f5f5f5",
                                    },
                                    cursor: "not-allowed;",
                                    fontStyle: "italic",
                                  }}
                                >
                                  <ListItemText
                                    primary="Providers and planners are not available for this location. Kindly select a different location."
                                    sx={{ color: "#767373" }}
                                  />
                                </ListItem>
                              )}
                          </List>
                        </Paper>
                      )}
                  </Box>
                </Box>
              </Box>

              {/* Headline */}
              <Box sx={{ mt: 3 }}>
                <Typography
                  sx={{
                    color: "#1A494D",
                    fontWeight: 500,
                    fontSize: { xs: "40px", sm: "50px", md: "56px" },
                    paddingLeft: { xs: 3, sm: 0 },
                  }}
                >
                  A Clear Path Forward.
                  <br />
                  Guided Support,
                  <br />
                  From Start to Finish.
                </Typography>
                <Typography
                  sx={{
                    color: "#1A494D",
                    fontSize: { xs: "14px", md: "18px" },
                    mt: 1,
                    paddingLeft: { xs: 3, sm: 0 },
                  }}
                >
                  MemorialGo helps you connect, decide, and plan— all in one
                  place.
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Tabs Section */}
        <Container
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "start",
            paddingBottom: { xs: "1.5rem", md: "2rem" },
            px: { xs: 2, sm: 3, md: 4 },
            maxWidth: { md: 1262 },
          }}
        >
          <Box className="tabs" sx={{ mt: { xs: 2, md: 4 }, width: "100%" }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                "& .MuiTab-root": {
                  color: "#15585D !important",
                  borderRadius: "0.5rem 0.5rem 0 0",
                  bgcolor: "#C7C7C7",
                  marginRight: { xs: "0.5rem", md: "1rem" },
                  minHeight: { xs: "2rem", md: "2.5rem" },
                  marginTop: "1rem",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  width: { xs: "9rem", sm: "12rem", md: "12rem" },
                  fontSize: { xs: "0.75rem", sm: "0.9rem", md: "1rem" },
                },
                "& .Mui-selected": {
                  backgroundColor: "#14585D !important",
                  color: "#fff !important",
                },
              }}
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons="auto"
              TabIndicatorProps={{
                style: { display: "none" },
              }}
            >
              <Tab
                label="Immediate Need"
                icon={<WatchLaterOutlined />}
                iconPosition="start"
              />
              <Tab
                label="Preneed"
                icon={<CalendarMonthOutlined />}
                iconPosition="start"
              />
            </Tabs>

            <Box
              sx={{
                p: { xs: 2, md: 3 },
                paddingBottom: { md: 2 },
                backgroundColor: "#15585D",
                borderBottomLeftRadius: "8px !important",
                borderBottomRightRadius: "8px !important",
                borderTopRightRadius: "8px !important",
              }}
            >
              {tabValue === 0 ? (
                <Box>
                  <Typography
                    sx={{
                      mb: 2,
                      color: "#fff !important",
                      fontSize: {
                        xs: "18px !important",
                        md: "24px !important",
                      },
                      fontWeight: 400,
                      textAlign: { xs: "left", sm: "left" },
                    }}
                  >
                    Get help with arrangements needed right now.
                  </Typography>
                  <Box
                    component="form"
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                        lg: "1fr 1fr 1fr 1fr",
                      },
                      gap: 2,
                      mx: "auto",
                      maxWidth: 1200,
                    }}
                    noValidate
                  >
                    <Box>
                      <Typography
                        sx={{
                          color: "#fff !important",
                          fontSize: "18px !important",
                          fontWeight: 400,
                          mb: 0.5,
                        }}
                      >
                        Location{" "}
                        <Box component="span" sx={{ color: "red" }}>
                          *
                        </Box>
                      </Typography>
                      <Box sx={{ position: "relative", width: "100%" }}>
                        <TextField
                          name="displayLocation"
                          placeholder="Auto-detect or Manually Enter"
                          autoComplete="off"
                          value={manualCity}
                          onChange={handleLocationOnChange}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                sx={{ p: 0, m: 0 }}
                              >
                                <IconButton
                                  onClick={handleDetectLocation}
                                  edge="end"
                                  size="small"
                                  aria-label="detect location"
                                >
                                  <PlaceOutlined sx={{ fontSize: 18 }} />
                                </IconButton>
                              </InputAdornment>
                            ),
                            sx: {
                              height: 40,
                              fontSize: 16,
                              paddingRight: 0,
                            },
                          }}
                          variant="outlined"
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              paddingRight: 0.5,
                            },
                            "& .MuiInputAdornment-root": {
                              marginRight: 0,
                            },
                          }}
                          InputLabelProps={{ shrink: false }}
                        />

                        {/* Suggestions Dropdown */}
                        {suggestions.length > 0 && (
                          <Box
                            className="search-suggestions-box"
                            sx={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              right: 0,
                              zIndex: 9999,
                              border: "1px solid #ccc",
                              backgroundColor: "#FFF",
                              boxShadow: 2,
                              maxHeight: 115,
                              overflowY: "auto",
                              borderTop: "none",
                            }}
                          >
                            {suggestions.map((suggestion, index) => (
                              <Box
                                key={index}
                                className="suggestion-item"
                                onClick={() =>
                                  handleSuggestionClick(suggestion)
                                }
                                sx={{
                                  px: 2,
                                  py: 0.5,
                                  cursor: "pointer",
                                  "&:hover": {
                                    backgroundColor: "#f5f5f5",
                                  },
                                }}
                              >
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {suggestion.main}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#555" }}
                                >
                                  {suggestion.secondary}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>

                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#fff !important",
                          fontSize: "18px !important",
                          fontWeight: 400,
                          mb: 0.5,
                        }}
                      >
                        Name{" "}
                        <Box component="span" sx={{ color: "red" }}>
                          *
                        </Box>
                      </Typography>
                      <TextField
                        name="name"
                        placeholder="Enter Name"
                        autoComplete="off"
                        value={immediateNeedForm.name}
                        onChange={handleImmediateNeedChange}
                        variant="outlined"
                        fullWidth
                        error={
                          immediateNeedForm.name.trim().length > 0 &&
                          immediateNeedForm.name.trim().length < 2
                        }
                        helperText={
                          immediateNeedForm.name.trim().length > 0 &&
                          immediateNeedForm.name.trim().length < 2
                            ? "Name must be at least 2 characters"
                            : ""
                        }
                        sx={{
                          width: "100%",
                          "& .MuiFormHelperText-root": {
                            marginLeft: 0,
                            color: "#d32f2f",
                          },
                        }}
                        InputProps={{
                          sx: { height: 40 },
                        }}
                        InputLabelProps={{ shrink: false }}
                      />
                    </Box>

                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#fff !important",
                          fontSize: "18px !important",
                          fontWeight: 400,
                          mb: 0.5,
                        }}
                      >
                        Phone Number{" "}
                        <Box component="span" sx={{ color: "red" }}>
                          *
                        </Box>
                      </Typography>
                      <TextField
                        name="phone"
                        placeholder="Enter Phone Number"
                        autoComplete="off"
                        value={immediateNeedForm.phone}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val.length <= 10 && /^\d{0,10}$/.test(val)) {
                            setImmediateNeedForm((prev) => ({
                              ...prev,
                              phone: val,
                            }));
                          }
                        }}
                        variant="outlined"
                        fullWidth
                        sx={{ width: "100%" }}
                        InputProps={{
                          sx: {
                            height: 40,
                            paddingLeft: "2px",
                          },
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              sx={{
                                color: "#4B4F50",
                                marginLeft: "2px",
                              }}
                            >
                              <img
                                src={selectedCountry.flag}
                                alt={selectedCountry.isoCode}
                                style={{
                                  width: 32,
                                  height: 24,
                                  marginRight: 4,
                                }}
                              />
                              {selectedCountry.dialCode}
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#fff !important",
                          fontSize: "18px !important",
                          fontWeight: 400,
                          mb: 0.5,
                        }}
                      >
                        Service Categories
                        <Box component="span" sx={{ color: "red" }}>
                          *
                        </Box>
                      </Typography>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        sx={{ height: 40 }}
                      >
                        <Select
                          name="serviceType"
                          value={immediateNeedForm.serviceType}
                          onChange={handleImmediateNeedChange}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return (
                                <span style={{ color: "#9e9e9e" }}>
                                  -- Select Service Category --
                                </span>
                              );
                            }
                            // const selectedService = immediateNeedServices.find(
                            //   (service) => service.id === Number(selected)
                            // );
                            // return selectedService?.name || "";
                            const selectedCategory =
                              immediateNeedCategories.find(
                                (category) => category.id === Number(selected),
                              );
                            return selectedCategory?.name || "";
                          }}
                          sx={{
                            height: 40,
                            "& .MuiSelect-select": {
                              paddingY: "10px",
                            },
                          }}
                        >
                          {/* {immediateNeedServices?.map((service) => (
                            <MenuItem key={service.id} value={service.id}>
                              {service.name}
                            </MenuItem>
                          ))} */}
                          {immediateNeedCategories?.map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                              {category.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    <Box
                      sx={{
                        gridColumn: { xs: "1 / -1", sm: "1 / -1" },
                        display: "flex",
                        gap: 2,
                        mt: 2,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={handleImmediateNeedReset}
                        sx={{
                          textTransform: "none",
                          minWidth: 120,
                          bgcolor: "#fff",
                          color: "#000",
                          border: "1px solid #204346 !important",
                          "&:hover": {
                            color: "#000 !important",
                            border: "1px solid #204346 !important",
                          },
                        }}
                      >
                        Reset
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleImmediateNeedSubmit}
                        disabled={isImmediateNeedButtonDisabled}
                        sx={{
                          textTransform: "none",
                          minWidth: 120,
                          backgroundColor: "#EBD28D !important",
                          color: "#104145 !important",
                          opacity: isImmediateNeedButtonDisabled ? 0.9 : 1,
                          cursor: isImmediateNeedButtonDisabled
                            ? "not-allowed"
                            : "pointer",
                          "&.Mui-disabled": {
                            color: "#FFF !important",
                            cursor: "not-allowed",
                            backgroundColor: "#204346 !important",
                          },
                        }}
                      >
                        Find Services
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography
                    sx={{
                      mb: 2,
                      color: "#fff !important",
                      fontSize: {
                        xs: "18px !important",
                        md: "24px !important",
                      },
                      fontWeight: 400,
                      textAlign: { xs: "left", sm: "left" },
                    }}
                  >
                    Make arrangements in advance for peace of mind.
                  </Typography>
                  <Box
                    component="form"
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                        lg: "1fr 1fr 1fr 1fr",
                      },
                      gap: 2,
                      mx: "auto",
                      maxWidth: 1200,
                    }}
                    noValidate
                  >
                    {/* Location */}
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#ffffff !important",
                          fontSize: "18px !important",
                          fontWeight: 400,
                          mb: 0.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.3,
                        }}
                      >
                        Location{" "}
                        <Box component="span" sx={{ color: "red" }}>
                          *
                        </Box>
                      </Typography>
                      <Box sx={{ position: "relative", width: "100%" }}>
                        <TextField
                          name="displayLocation"
                          placeholder="Auto-detect or Manually Enter"
                          autoComplete="off"
                          value={manualCity}
                          onChange={handleLocationOnChange}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                sx={{ p: 0, m: 0 }}
                              >
                                <IconButton
                                  onClick={handleDetectLocation}
                                  edge="end"
                                  size="small"
                                  aria-label="detect location"
                                >
                                  <PlaceOutlined sx={{ fontSize: 18 }} />
                                </IconButton>
                              </InputAdornment>
                            ),
                            sx: {
                              height: 40,
                              fontSize: 16,
                              paddingRight: 0,
                            },
                          }}
                          variant="outlined"
                          fullWidth
                          sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-root": {
                              paddingRight: 0.5,
                            },
                            "& .MuiInputAdornment-root": {
                              marginRight: 0,
                            },
                          }}
                          InputLabelProps={{ shrink: false }}
                        />
                        {/* Suggestions Dropdown */}
                        {suggestions.length > 0 && (
                          <Box
                            className="search-suggestions-box"
                            sx={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              right: 0,
                              zIndex: 999,
                              border: "1px solid #ccc",
                              backgroundColor: "#FFF",
                              boxShadow: 2,
                              maxHeight: 250,
                              overflowY: "auto",
                              borderTop: "none",
                            }}
                          >
                            {suggestions.map((suggestion, index) => (
                              <Box
                                key={index}
                                className="suggestion-item"
                                onClick={() =>
                                  handleSuggestionClick(suggestion)
                                }
                                sx={{
                                  px: 2,
                                  py: 1,
                                  cursor: "pointer",
                                  "&:hover": {
                                    backgroundColor: "#f5f5f5",
                                  },
                                }}
                              >
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {suggestion.main}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#555" }}
                                >
                                  {suggestion.secondary}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Name */}
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#ffffff !important",
                          fontSize: "18px !important",
                          fontWeight: 400,
                          mb: 0.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.3,
                        }}
                      >
                        Name{" "}
                        <Box component="span" sx={{ color: "red" }}>
                          *
                        </Box>
                      </Typography>
                      <TextField
                        name="name"
                        value={preneedForm.name}
                        onChange={handlePreneedChange}
                        placeholder="Enter Name"
                        autoComplete="off"
                        variant="outlined"
                        fullWidth
                        error={
                          preneedForm.name.trim().length > 0 &&
                          preneedForm.name.trim().length < 2
                        }
                        helperText={
                          preneedForm.name.trim().length > 0 &&
                          preneedForm.name.trim().length < 2
                            ? "Name must be at least 2 characters"
                            : ""
                        }
                        sx={{
                          width: "100%",
                          "& .MuiFormHelperText-root": {
                            marginLeft: 0,
                            color: "#d32f2f",
                          },
                        }}
                        InputProps={{
                          sx: { height: 40 },
                        }}
                        InputLabelProps={{ shrink: false }}
                      />
                    </Box>

                    {/* Phone Number */}
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#ffffff !important",
                          fontSize: "18px !important",
                          fontWeight: 400,
                          mb: 0.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.3,
                        }}
                      >
                        Phone Number{" "}
                        <Box component="span" sx={{ color: "red" }}>
                          *
                        </Box>
                      </Typography>
                      <TextField
                        name="phone"
                        placeholder="Enter Phone Number"
                        autoComplete="off"
                        value={preneedForm.phone}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val.length <= 10 && /^\d{0,10}$/.test(val)) {
                            setPreneedForm((prev) => ({
                              ...prev,
                              phone: val,
                            }));
                          }
                        }}
                        variant="outlined"
                        fullWidth
                        sx={{ width: "100%" }}
                        InputProps={{
                          sx: {
                            height: 40,
                            paddingLeft: "2px",
                          },
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              sx={{
                                color: "#4B4F50",
                                marginLeft: "2px",
                              }}
                            >
                              <img
                                src={selectedCountry.flag}
                                alt={selectedCountry.isoCode}
                                style={{
                                  width: 32,
                                  height: 24,
                                  marginRight: 4,
                                }}
                              />
                              {selectedCountry.dialCode}
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    {/* Type of Service */}
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#ffffff !important",
                          fontSize: "18px !important",
                          fontWeight: 400,
                          mb: 0.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.3,
                        }}
                      >
                        Service Categories
                        <Box component="span" sx={{ color: "red" }}>
                          *
                        </Box>
                      </Typography>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        sx={{ height: 40 }}
                      >
                        <Select
                          name="serviceType"
                          value={preneedForm.serviceType}
                          onChange={handlePreneedChange}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return (
                                <span style={{ color: "#9e9e9e" }}>
                                  -- Select Service Category --
                                </span>
                              );
                            }
                            // const selectedService = preneedServices.find(
                            //   (service) => service.id === Number(selected)
                            // );
                            // return selectedService?.name || "";
                            const selectedCategory = preNeedCategories.find(
                              (category) => category.id === Number(selected),
                            );
                            return selectedCategory?.name || "";
                          }}
                          sx={{
                            height: 40,
                            "& .MuiSelect-select": {
                              paddingY: "10px",
                            },
                          }}
                        >
                          {/* {preneedServices?.map((service) => (
                            <MenuItem key={service.id} value={service.id}>
                              {service.name}
                            </MenuItem>
                          ))} */}
                          {preNeedCategories?.map((category: any) => (
                            <MenuItem key={category.id} value={category.id}>
                              {category.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Date and Time */}
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#ffffff !important",
                          fontSize: "18px !important",
                          fontWeight: 400,
                          display: "flex",
                          alignItems: "center",
                          mb: "0 !important",
                          gap: 0.3,
                        }}
                      >
                        Date and Time{" "}
                        <Box component="span" sx={{ color: "red" }}>
                          *
                        </Box>
                      </Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DemoContainer
                          components={["DateTimePicker"]}
                          sx={{
                            marginTop: 0.3,
                            paddingTop: 0,
                          }}
                        >
                          <DatePicker
                            key={datePickerKey}
                            value={selectedDate}
                            onChange={handleDateChange}
                            format="MM/DD/YYYY"
                            disablePast
                            slots={{ actionBar: TimeWindowActionBar }}
                            slotProps={{
                              textField: {
                                placeholder: "-- Select Date --",
                                size: "small",
                                variant: "outlined",
                                InputProps: { sx: { height: 40 } },
                              },
                              actionBar: { timeWindow, setTimeWindow },
                            }}
                          />
                        </DemoContainer>
                      </LocalizationProvider>
                    </Box>

                    {/* Buttons */}
                    <Box
                      sx={{
                        gridColumn: { xs: "1 / -1", sm: "1 / -1" },
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        mt: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: {
                            xs: "column",
                            sm: "row",
                          },
                          gap: 2,
                          width: {
                            xs: "100%",
                            sm: "auto",
                          },
                        }}
                      >
                        <Button
                          variant="outlined"
                          onClick={handlePreneedReset}
                          sx={{
                            textTransform: "none",
                            minWidth: 120,
                            height: 40,
                            width: {
                              xs: "100%",
                              sm: "auto",
                            },
                            bgcolor: "#fff",
                            color: "#000",
                            border: "1px solid #204346 !important",
                            "&:hover": {
                              color: "#000 !important",
                              border: "1px solid #204346 !important",
                            },
                          }}
                        >
                          Reset
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handlePreneedSubmit}
                          disabled={isPreneedButtonDisabled}
                          sx={{
                            textTransform: "none",
                            minWidth: 120,
                            backgroundColor: "#EBD28D !important",
                            color: "#104145 !important",
                            opacity: isPreneedButtonDisabled ? 0.9 : 1,
                            cursor: isPreneedButtonDisabled
                              ? "not-allowed"
                              : "pointer",
                            "&.Mui-disabled": {
                              color: "#FFF !important",
                              cursor: "not-allowed",
                              backgroundColor: "#204346 !important",
                            },
                          }}
                        >
                          Find Services
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Container>
      </Container>
    </Box>
  );
}
